// src\themes\lino\components\ScrollGallery.tsx

'use client';

import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';
import 'swiper/css/free-mode';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { cn } from '@/core/lib/utils';
import { textGroupVariants, textItemVariants } from './animations/textReveal';

const isVideoUrl = (url?: string) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

type ShowcaseItem = {
    title?: string;
    description?: string;
    link?: string;
    // عرض کارت نسبت به عرض ردیف؛ دقیقاً مثل عرض ویجت تصویر (1/1, 1/2, 1/3, 2/3)
    // چون توی اپل هم عکس‌ها هم‌عرض نیستن
    width?: '1/1' | '1/2' | '1/3' | '1/4' | '2/3' | '3/4';
    // جهت تصویر: عمودی (3:4، پیش‌فرض) یا افقی (16:9)
    orientation?: 'horizontal' | 'vertical';
    image?: { url: string; description?: string };
};

type ScrollGalleryData = {
    title?: string;
    description?: string;
    items?: ShowcaseItem[];
};

// عرض هر کارت به‌صورت درصدی از عرض ردیف اسکرول (نه عرض کل صفحه)، با کمی
// جمع‌وجورتر شدن توی موبایل تا همیشه یه‌ذره از کارت بعدی معلوم باشه
const SLIDE_WIDTH_CLASS: Record<string, string> = {
    '1/1': 'w-[88%]! sm:w-[78%]! lg:w-[70%]!',
    '3/4': 'w-[84%]! sm:w-[70%]! lg:w-[60%]!',
    '2/3': 'w-[80%]! sm:w-[62%]! lg:w-[52%]!',
    '1/2': 'w-[72%]! sm:w-[46%]! lg:w-[38%]!',
    '1/3': 'w-[62%]! sm:w-[33%]! lg:w-[27%]!',
    '1/4': 'w-[56%]! sm:w-[25%]! lg:w-[21%]!',
};

export default function ScrollGallery({ data }: { data: ScrollGalleryData }) {
    const items = data?.items || [];
    const swiperRef = useRef<SwiperClass | null>(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    if (items.length === 0) return null;

    const updateEdges = (swiper: SwiperClass) => {
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
    };

    return (
        <div className="col-span-12">
            {(data.title || data.description) && (
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                    variants={textGroupVariants}
                    className="mb-6 lg:mb-8 space-y-1.5"
                >
                    {data.title && (
                        <motion.h2
                            variants={textItemVariants}
                            className="text-xl lg:text-3xl font-light text-primary-900 tracking-tight"
                        >
                            {data.title}
                        </motion.h2>
                    )}
                    {data.description && (
                        <motion.p variants={textItemVariants} className="text-[13px] text-primary-500">
                            {data.description}
                        </motion.p>
                    )}
                </motion.div>
            )}

            <div className="relative group/showcase">
                <Swiper
                    modules={[Navigation, Mousewheel, FreeMode]}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        updateEdges(swiper);
                    }}
                    onSlideChange={updateEdges}
                    onReachBeginning={updateEdges}
                    onReachEnd={updateEdges}
                    onFromEdge={updateEdges}
                    navigation={{ nextEl: '.showcase-next', prevEl: '.showcase-prev' }}
                    freeMode={{ enabled: true, sticky: true, momentumBounce: false }}
                    mousewheel={{ forceToAxis: true }}
                    grabCursor
                    slidesPerView="auto"
                    spaceBetween={20}
                    // ارتفاع خودِ کادر تصویر (نه کل کارت) ثابته؛ همه‌ی کارت‌ها صرف‌نظر از
                    // عرض یا جهت تصویرشون همین ارتفاع رو می‌گیرن و تصویر با object-cover
                    // توش کراپ می‌شه، پس هیچ عکسی کوتاه‌تر یا بلندتر از بقیه نمی‌شه
                    className="w-full !overflow-visible"
                >
                    {items.map((item, index) => {
                        const widthClass = SLIDE_WIDTH_CLASS[item.width || '1/2'];

                        const Content = (
                            <div className="group/card">
                                <div className="relative w-full h-[62vw] sm:h-[46vw] lg:h-[34vw] overflow-hidden rounded-2xl bg-primary-900">
                                    {item.image?.url && (
                                        isVideoUrl(item.image.url) ? (
                                            <video
                                                src={item.image.url}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover/card:scale-[1.035]"
                                            />
                                        ) : (
                                            <Image
                                                src={item.image.url}
                                                alt={item.image.description || item.title || ''}
                                                fill
                                                sizes="(max-width: 640px) 90vw, (max-width: 1024px) 60vw, 35vw"
                                                className="object-cover transition-transform duration-[900ms] ease-out group-hover/card:scale-[1.035]"
                                            />
                                        )
                                    )}
                                </div>
                                {(item.title || item.description) && (
                                    <p className="mt-4 text-[13.5px] leading-6 text-primary-900">
                                        {item.title && (
                                            <span className="font-primary-bold">{item.title} </span>
                                        )}
                                        {item.description && (
                                            <span className="text-primary-600">{item.description}</span>
                                        )}
                                    </p>
                                )}
                            </div>
                        );

                        return (
                            <SwiperSlide key={index} className={cn('!h-auto', widthClass)}>
                                {item.link ? <Link href={item.link}>{Content}</Link> : Content}
                            </SwiperSlide>
                        );
                    })}
                </Swiper>

                <button
                    aria-label="قبلی"
                    className={cn(
                        'showcase-prev absolute top-[38%] -translate-y-1/2 ltr:left-0 rtl:right-0 z-10',
                        'flex items-center justify-center size-10 rounded-full bg-primary-0/90 shadow-lg backdrop-blur',
                        'transition-opacity duration-300 opacity-0 group-hover/showcase:opacity-100',
                        'disabled:hidden ltr:-translate-x-4 rtl:translate-x-4'
                    )}
                    disabled={isBeginning}
                >
                    <ChevronLeft className="size-5 stroke-[1.5] text-primary-900 rtl:rotate-180" />
                </button>
                <button
                    aria-label="بعدی"
                    className={cn(
                        'showcase-next absolute top-[38%] -translate-y-1/2 ltr:right-0 rtl:left-0 z-10',
                        'flex items-center justify-center size-10 rounded-full bg-primary-0/90 shadow-lg backdrop-blur',
                        'transition-opacity duration-300 opacity-0 group-hover/showcase:opacity-100',
                        'disabled:hidden ltr:translate-x-4 rtl:-translate-x-4'
                    )}
                    disabled={isEnd}
                >
                    <ChevronRight className="size-5 stroke-[1.5] text-primary-900 rtl:rotate-180" />
                </button>
            </div>
        </div>
    );
}